/**
 * LightProbeGridComponent - Scene component for global illumination via light probe grids.
 * Wraps Three.js LightProbeGrid for spherical harmonic-based indirect lighting.
 * Supports WebGL and WebGPU baking with configurable bounds and resolution.
 */

import * as THREE from 'three';
import { LightProbeGridCubeMapHelper } from 'three/addons/helpers/LightProbeGridCubeMapHelper.js';
import { LightProbeGridHelper } from 'three/addons/helpers/LightProbeGridHelper.js';
import { LightProbeGrid } from 'three/addons/lighting/LightProbeGrid.js';

import { EngineClass } from '../../systems/ClassRegistry.js';
import { Delegate } from '../../utils/Delegate.js';
import { callable, property } from '../../utils/serialization/decorator.js';
import { SceneComponent, SceneComponentOptions } from '../SceneComponent.js';


export type ProbeVisualizationMode = 'none' | 'sh' | 'cubemap';

export interface LightProbeGridComponentOptions extends SceneComponentOptions {
  /** Size of the cube map for SH generation */
  cubeMapSize?: number;
  /** Width of the probe grid bounds */
  boundsX?: number;
  /** Height of the probe grid bounds */
  boundsY?: number;
  /** Depth of the probe grid bounds */
  boundsZ?: number;
  /** Number of probes along X axis */
  countX?: number;
  /** Number of probes along Y axis */
  countY?: number;
  /** Number of probes along Z axis */
  countZ?: number;
  /** Size of probe visualization spheres */
  probeSize?: number;
  /** Visualization mode: spherical harmonics or cubemap */
  visualizationMode?: ProbeVisualizationMode;
}

export type LightProbeBakeStatus = 'idle' | 'baking' | 'complete' | 'error';

export interface LightProbeBakeStatusInfo {
  status: LightProbeBakeStatus;
  message: string;
  durationMs?: number;
}

export type LightProbeBakeStatusCallback = (info: LightProbeBakeStatusInfo) => void;

@EngineClass('LightProbeGridComponent')
export class LightProbeGridComponent extends SceneComponent {

  private _probeSize: number = 1.0;
  private _boundsX: number = 50;
  private _boundsY: number = 20;
  private _boundsZ: number = 50;
  private _countX: number = 5;
  private _countY: number = 3;
  private _countZ: number = 5;
  private _visualizationMode: ProbeVisualizationMode = 'none';

  private lightProbes: LightProbeGrid | null = null;
  private probesHelper: LightProbeGridHelper | LightProbeGridCubeMapHelper | null = null;
  private rebakeTimer: ReturnType<typeof setTimeout> | null = null;
  private _isBaking: boolean = false;
  private _isGridDirty: boolean = false;
  private debugVolumeMesh: THREE.Mesh | null = null;

  @property({ type: 'number', min: 16, max: 1024 })
  public cubeMapSize: number = 16;

  @property({ type: 'number', min: 0.1, max: 2.0, step: 0.1 })
  public get probeSize(): number {
    return this._probeSize;
  }
  public set probeSize(value: number) {
    if (this._probeSize === value) return;
    this._probeSize = value;
    this.recreateProbeHelper();
  }

  @property({ type: 'number', min: 1, max: 200 })
  public get boundsX(): number {
    return this._boundsX;
  }
  public set boundsX(value: number) {
    if (this._boundsX === value) return;
    this._boundsX = value;
    this._isGridDirty = true;
    if (this.debugVolumeMesh) {
      this.debugVolumeMesh.scale.x = value;
    }
  }

  @property({ type: 'number', min: 1, max: 100 })
  public get boundsY(): number {
    return this._boundsY;
  }
  public set boundsY(value: number) {
    if (this._boundsY === value) return;
    this._boundsY = value;
    this._isGridDirty = true;
    if (this.debugVolumeMesh) {
      this.debugVolumeMesh.scale.y = value;
    }
  }

  @property({ type: 'number', min: 1, max: 200 })
  public get boundsZ(): number {
    return this._boundsZ;
  }
  public set boundsZ(value: number) {
    if (this._boundsZ === value) return;
    this._boundsZ = value;
    this._isGridDirty = true;
    if (this.debugVolumeMesh) {
      this.debugVolumeMesh.scale.z = value;
    }
  }

  @property({ type: 'number', min: 2, max: 20, step: 1 })
  public get countX(): number {
    return this._countX;
  }
  public set countX(value: number) {
    if (this._countX === value) return;
    this._countX = value;
    this._isGridDirty = true;
  }

  @property({ type: 'number', min: 2, max: 10, step: 1 })
  public get countY(): number {
    return this._countY;
  }
  public set countY(value: number) {
    if (this._countY === value) return;
    this._countY = value;
    this._isGridDirty = true;
  }

  @property({ type: 'number', min: 2, max: 20, step: 1 })
  public get countZ(): number {
    return this._countZ;
  }
  public set countZ(value: number) {
    if (this._countZ === value) return;
    this._countZ = value;
    this._isGridDirty = true;
  }

  @property({ type: 'enum', options: ['none', 'sh', 'cubemap'] })
  public get visualizationMode(): ProbeVisualizationMode {
    return this._visualizationMode;
  }
  public set visualizationMode(value: ProbeVisualizationMode) {
    if (this._visualizationMode === value) return;
    this._visualizationMode = value;
    this.onVisualizationModeChanged();
  }

  public get isBaking(): boolean {
    return this._isBaking;
  }

  /** Delegate called with status info when baking starts, completes, or errors. */
  public readonly onBakeStatus: Delegate<[LightProbeBakeStatusInfo], void> = new Delegate();

  public override initialize(options?: LightProbeGridComponentOptions): void {
    super.initialize(options);
    this.cubeMapSize = options?.cubeMapSize ?? this.cubeMapSize;
    this._probeSize = options?.probeSize ?? this._probeSize;
    this._boundsX = options?.boundsX ?? this._boundsX;
    this._boundsY = options?.boundsY ?? this._boundsY;
    this._boundsZ = options?.boundsZ ?? this._boundsZ;
    this._countX = options?.countX ?? this._countX;
    this._countY = options?.countY ?? this._countY;
    this._countZ = options?.countZ ?? this._countZ;
    this._visualizationMode = options?.visualizationMode ?? this._visualizationMode;
  }

  /**
   * Schedules a rebake of the light probes after the specified delay.
   * Multiple calls will reset the timer.
   */
  public scheduleRebake(delay: number = 100): void {
    if (this.rebakeTimer) {
      clearTimeout(this.rebakeTimer);
    }

    this.rebakeTimer = setTimeout(() => {
      void this.bakeLightProbes();
    }, delay);
  }

  /**
   * Immediately bakes the light probes if enabled.
   * Returns a promise that resolves when baking completes or errors.
   */
  @callable({ label: 'Bake Light Probes', tooltip: 'Bake lighting data for all probes in the grid' })
  public async bakeLightProbes(): Promise<void> {
    if (this._isBaking) {
      return;
    }

    if (!this.lightProbes || this._isGridDirty) {
      this.createProbes();
      this._isGridDirty = false;
    }

    const world = this.getWorld();
    const renderer = world?.getRenderer();
    if (!renderer) {
      this.reportBakeStatus({ status: 'error', message: 'No renderer available' });
      return;
    }

    // Get the native WebGL or WebGPU renderer
    const nativeRenderer = renderer.asWebGL() ?? renderer.asWebGPU();
    if (!nativeRenderer) {
      this.reportBakeStatus({ status: 'error', message: 'No WebGL or WebGPU renderer available' });
      return;
    }

    this._isBaking = true;
    const bakeStart = performance.now();
    this.reportBakeStatus({ status: 'baking', message: 'Baking light probes...' });

    // Hide helper during baking
    if (this.probesHelper) {
      this.probesHelper.visible = false;
    }

    // Retain cubemaps if using cube map visualization mode
    const bakeOptions = {
      cubemapSize: this.cubeMapSize,
      retainCubemaps: this._visualizationMode === 'cubemap',
    };
    await this.lightProbes!.bake(nativeRenderer, world!.scene, bakeOptions);
    this.recreateProbeHelper();

    const durationMs = performance.now() - bakeStart;
    const durationSec = (durationMs / 1000).toFixed(2);
    const message = `Light probes baked in ${durationSec}s`;
    this.reportBakeStatus({ status: 'complete', message, durationMs });

    this._isBaking = false;
    // Show helper after baking
    if (this.probesHelper) {
      this.probesHelper.visible = this._visualizationMode !== 'none';
    }
  }

  public override endPlay(): void {
    this.disposeProbes();
    super.endPlay();
  }

  /**
   * Creates the light probe grid and attaches it to this component.
   * Called automatically during beginPlay.
   */
  protected createProbes(): void {
    this.disposeProbes();

    // Create light probe grid (constructor takes width, height, depth, probesX, probesY, probesZ)
    this.lightProbes = new LightProbeGrid(
      this.boundsX,
      this.boundsY,
      this.boundsZ,
      this.countX,
      this.countY,
      this.countZ
    );
    // this.lightProbes.position.y = this.boundsY / 2;
    this.lightProbes.setTransient(true);
    // don't inherit the rotation and scale from the parent
    // only the world position is respected in LightProbeGrid
    this.lightProbes.useAbsoluteRotation = true;
    this.lightProbes.useAbsoluteScale = true;
    this.add(this.lightProbes);

    // Create helper
    this.recreateProbeHelper();
  }

  /**
   * Disposes the light probe grid and helper.
   */
  protected disposeProbes(): void {
    if (this.rebakeTimer) {
      clearTimeout(this.rebakeTimer);
      this.rebakeTimer = null;
    }

    if (this.probesHelper) {
      this.probesHelper.removeFromParent();
      this.probesHelper.dispose();
      this.probesHelper = null;
    }

    if (this.lightProbes) {
      this.lightProbes.removeFromParent();
      this.lightProbes.dispose();
      this.lightProbes = null;
    }
  }

  /**
   * Called when visualizationMode changes. Handles side effects like
   * recreating the helper or scheduling a rebake for cubemap mode.
   */
  private onVisualizationModeChanged(): void {
    if (!this.lightProbes) return;
    if (this._visualizationMode === 'cubemap') {
      // Need to rebake to get cubemaps when switching to cubemap mode
      this.scheduleRebake(100);
    } else {
      this.recreateProbeHelper();
    }
  }

  private recreateProbeHelper(): void {
    if (this.probesHelper) {
      this.probesHelper.removeFromParent();
      this.probesHelper.dispose();
      this.probesHelper = null;
    }

    if (!this.lightProbes || this._visualizationMode === 'none') return;

    // Detect if using WebGPU for helper configuration
    const webgpu = !!this.getWorld()?.getRenderer()?.asWebGPU();

    if (this._visualizationMode === 'cubemap') {
      this.probesHelper = new LightProbeGridCubeMapHelper(
        this.lightProbes,
        this._probeSize * 2,
        { webgpu }
      );
    } else {
      this.probesHelper = new LightProbeGridHelper(
        this.lightProbes,
        this._probeSize,
        { webgpu }
      );
    }
    this.probesHelper.setTransient(true);
    // the helper deals with grid location in world space so no need to inherit the transform
    this.probesHelper.useAbsolutePosition = true;
    this.probesHelper.useAbsoluteRotation = true;
    this.probesHelper.useAbsoluteScale = true;
    this.add(this.probesHelper);
  }

  private reportBakeStatus(info: LightProbeBakeStatusInfo): void {
    console.log(info);
    this.onBakeStatus.invoke(info);
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override onEditorAddToWorld(): void {
    super.onEditorAddToWorld();
    // the debug mesh is invisible but the editor outliner includes it so we can still see the bounds
    this.debugVolumeMesh = new THREE.Mesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({ color: 0x00ff00, visible: false })
    );
    this.debugVolumeMesh.setTransient(true);
    this.debugVolumeMesh.scale.set(this.boundsX, this.boundsY, this.boundsZ);
    this.add(this.debugVolumeMesh);
  }

  public override onEditorRemoveFromWorld(): void {
    if (this.debugVolumeMesh) {
      this.debugVolumeMesh.removeFromParent();
      this.debugVolumeMesh.geometry.dispose();
      this.debugVolumeMesh = null;
    }
    this.disposeProbes();
    super.onEditorRemoveFromWorld();
  }

  public override getEditorClassIcon(): string | null {
    return 'Icon_Light';
  }
}
